import React, { useEffect } from 'react';
import { Tooltip } from '@cherre-frontend/ui';
import {
  TextareaAutosize,
  Theme,
  Radio,
  FormControlLabel,
  Grid,
  RadioGroup,
  TextField,
  Typography,
} from '@material-ui/core';
import Autocomplete from '@mui/material/Autocomplete';
import { makeStyles } from '@material-ui/styles';
import { SingleDealModalProps } from 'src/services/DealcloudV2/types';
import { Field, FieldType } from '../type';

type DealCloudFormProps = {
  fields: Field[];
  form: any;
  propertyData?: SingleDealModalProps;
  setForm: React.Dispatch<React.SetStateAction<object>>;
};

const useStyles = makeStyles<Theme>((theme) => ({
  grid: {
    '&.MuiGrid-spacing-xs-4 > .MuiGrid-item': {
      padding: '10px 16px',
    },
  },
  key: {
    fontWeight: 500,
    fontSize: 14,
    display: 'flex',
  },
  required: {
    color: 'red',
    paddingLeft: '4px',
  },
  tooltip: {
    top: 0,
  },
  textArea: {
    ...theme.typography.body1,
    padding: '7px 8px 8px',
    border: '1px solid #9E9E9E',
    borderRadius: 4,
    fontSize: 14,
    width: '100%',
    '&:focus': {
      outline: `${theme.palette.primary.main} auto 1px`,
    },
  },
  numberField: {
    width: '100%',
    '& .MuiInputBase-root > input': {
      padding: '10px 8px 11px',
    },
  },
}));

const DealCloudForm: React.FC<DealCloudFormProps> = ({
  fields,
  form,
  setForm,
}) => {
  const classes = useStyles();

  useEffect(() => {
    fields.map((field) => {
      if (field.currentValue) {
        setForm((prevState) => ({
          ...prevState,
          [field.name]: field.currentValue,
        }));
      }
    });
  }, []);

  const sortedFields = fields.sort((a, b) => {
    if (a.createEditDialogOptions.order > b.createEditDialogOptions.order) {
      return 1;
    } else if (
      a.createEditDialogOptions.order === b.createEditDialogOptions.order
    ) {
      return 0;
    } else if (
      a.createEditDialogOptions.order < b.createEditDialogOptions.order
    ) {
      return -1;
    }
    return 0;
  });

  return (
    <Grid container spacing={4} className={classes.grid}>
      {sortedFields
        .filter((field) => !field.isAddress)
        .map((field, index) => {
          return (
            <Grid
              item
              key={`field-${index}`}
              xs={
                field.createEditDialogOptions.fullWidth ||
                field.createEditDialogOptions.type === FieldType.RADIO
                  ? 12
                  : 6
              }
            >
              <Typography className={classes.key}>
                {field.label}
                {field.isRequired && (
                  <Tooltip
                    title='This is a required field.'
                    placement='bottom'
                    slotProps={{
                      popper: {
                        modifiers: [
                          {
                            name: 'offset',
                            options: {
                              offset: [0, -14],
                            },
                          },
                        ],
                      },
                    }}
                  >
                    <Typography className={classes.required}>*</Typography>
                  </Tooltip>
                )}
              </Typography>
              {(() => {
                switch (field.createEditDialogOptions.type) {
                  case FieldType.TEXT:
                    return (
                      <TextareaAutosize
                        className={classes.textArea}
                        value={form[field.name] ?? ''}
                        placeholder={field.label}
                        onChange={(e) =>
                          setForm((prevState) => ({
                            ...prevState,
                            [field.name]: e.target.value,
                          }))
                        }
                      />
                    );
                  case FieldType.NUMBER:
                    return (
                      <TextField
                        className={classes.numberField}
                        type='number'
                        variant='outlined'
                        value={form[field.name] ?? ''}
                        placeholder={field.label}
                        onChange={(e) => {
                          setForm((prevState) => {
                            if (!e.target.value) {
                              delete prevState[field.name];
                              return { ...prevState };
                            }
                            return {
                              ...prevState,
                              [field.name]: Number(e.target.value),
                            };
                          });
                        }}
                      />
                    );
                  case FieldType.RADIO:
                    return (
                      <RadioGroup
                        row
                        value={form[field.name]}
                        onChange={(_, value) =>
                          setForm((prevState) => ({
                            ...prevState,
                            [field.name]: Number(value),
                          }))
                        }
                      >
                        {field.options?.map((option) => (
                          <FormControlLabel
                            key={option.id}
                            value={option.id.toString()}
                            control={<Radio color='primary' />}
                            label={option.name}
                          />
                        ))}
                      </RadioGroup>
                    );
                  case FieldType.SELECT:
                    return (
                      <Autocomplete
                        value={form[field.name] ?? undefined}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            variant='outlined'
                            size='small'
                            placeholder='Select'
                            style={{ backgroundColor: 'white' }}
                          />
                        )}
                        onChange={(_, value) =>
                          setForm((prevState) => ({
                            ...prevState,
                            [field.name]: value ? value.id : '',
                          }))
                        }
                        isOptionEqualToValue={(opt, value) =>
                          opt.id === value.id
                        }
                        getOptionLabel={(opt) => opt.name}
                        options={field.options ?? []}
                      />
                    );
                  default:
                    return;
                }
              })()}
            </Grid>
          );
        })}
    </Grid>
  );
};

export default DealCloudForm;
